import * as React from 'react';
import { t } from '@reos/i18n';
import { EmptyState } from '@redhand/ui';
import { DomainApiError } from '../lib/api-error.js';
import { SupportWidget } from '../components/SupportWidget.js';
import {
  emailCampaignsCsvUrl,
  getEmailCampaignOverview,
  getEmailCampaignTemplatePreview,
  setEmailCampaignCap,
  startEmailCampaign,
  stopEmailCampaign,
  type EmailCampaignDto,
  type EmailCampaignOverview,
  type EmailTemplatePreviewDto,
  type SendingDomainDto,
} from '../lib/email-campaign-api.js';

/**
 * Krok KAMP2 — `/kampane`: platformní přehled e-mailové kampaně.
 *
 * Co je na obrazovce a PROČ právě tohle:
 *  * **trychtýř** odesláno → doručeno → klik → návštěva → registrace →
 *    první report → placený tarif (osa zadání bodu 9, doslova),
 *  * **STOP** — tlačítko, které zastaví odesílání okamžitě. Je to `POST`
 *    měnící stav v DB, ne „flag v paměti“: pracovníci ho musí vidět bez
 *    restartu,
 *  * **ruční posun denního stropu** (plán 50 → 100 → 250 → 500),
 *  * **stav odesílacích domén** včetně zahřívání (bod 10),
 *  * **export CSV** (odkaz, ne fetch — viz `emailCampaignsCsvUrl`).
 *
 * Data se nenačítají, dokud uživatel nemá roli owner/admin (defense in depth;
 * API hlídá stejnou branu a vrací 403).
 */

const CAP_PLAN = [50, 100, 250, 500] as const;

export function KampanePage(): React.JSX.Element {
  const [overview, setOverview] = React.useState<EmailCampaignOverview | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  /**
   * Krok UXF4 bod 9 (nalez N12 reauditu GPT 10. 10. 2026) — PRAZDNY STAV MA
   * TLACITKO. Do UXF4 veta jen rekla, ze kampan zalozi podpora, a odkazala
   * na „Nahlásit problém vpravo dole"; pozadavek tedy koncil tim, ze clovek
   * musel hledat jiny ovladaci prvek. Kanal zustava TENTYZ (`SupportWidget`
   * v ovladanem rezimu, vzor z `portaly.tsx` bodu UXF2 7) — zadne nove API.
   */
  const [supportOpen, setSupportOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOverview(await getEmailCampaignOverview());
    } catch (e) {
      setError(
        e instanceof DomainApiError ? e.message : t('webAdmin.kampane.errLoad', { locale: 'cs' }),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const run = React.useCallback(
    async (id: string, action: () => Promise<unknown>) => {
      setBusyId(id);
      setError(null);
      try {
        await action();
        await load();
      } catch (e) {
        setError(
          e instanceof DomainApiError
            ? e.message
            : t('webAdmin.kampane.errAction', { locale: 'cs' }),
        );
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  return (
    <div className="p-6" data-testid="kampane-page">
      <h1 className="mb-4 text-xl font-bold">{t('webAdmin.kampane.title', { locale: 'cs' })}</h1>

      {loading && (
        <p role="status" data-testid="kampane-loading">
          {t('webAdmin.kampane.loading', { locale: 'cs' })}
        </p>
      )}
      {error !== null && (
        <p role="alert" className="text-red-600" data-testid="kampane-error">
          {error}
        </p>
      )}

      {!loading && overview !== null && (
        <>
          <p data-testid="kampane-outbound-state">
            {overview.outboundSend
              ? t('webAdmin.kampane.outboundOn', { locale: 'cs' })
              : t('webAdmin.kampane.outboundOff', { locale: 'cs' })}
          </p>

          {overview.campaigns.length > 0 ? (
            <a
              className="rh-btn"
              href={emailCampaignsCsvUrl()}
              target="_blank"
              rel="noopener"
              data-testid="kampane-export-csv"
            >
              {t('webAdmin.kampane.exportCsv', { locale: 'cs' })}
            </a>
          ) : null}

          {overview.campaigns.length === 0 ? (
            <>
              <EmptyState
                title={t('webAdmin.kampane.emptyTitle', { locale: 'cs' })}
                description={t('webAdmin.kampane.emptyText', { locale: 'cs' })}
              />
              <button
                type="button"
                className="rh-btn rh-btn--primary"
                onClick={() => { setSupportOpen(true); }}
                data-testid="kampane-request-support"
              >
                {t('webAdmin.kampane.requestSupport', { locale: 'cs' })}
              </button>
              <SupportWidget open={supportOpen} onOpenChange={setSupportOpen} />
            </>
          ) : (
            overview.campaigns.map((campaign) => (
              <CampaignCard
                key={campaign.id}
                campaign={campaign}
                busy={busyId === campaign.id}
                onStop={() => {
                  void run(campaign.id, () => stopEmailCampaign(campaign.id));
                }}
                onStart={() => {
                  void run(campaign.id, () => startEmailCampaign(campaign.id));
                }}
                onCap={(dailyCap) => {
                  void run(campaign.id, () => setEmailCampaignCap(campaign.id, dailyCap));
                }}
              />
            ))
          )}

          {overview.domains.length > 0 && (
            <section data-testid="kampane-domains">
              <h2 className="mb-2 mt-6 text-lg font-semibold">
                {t('webAdmin.kampane.domains', { locale: 'cs' })}
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left">
                      {t('webAdmin.kampane.domainName', { locale: 'cs' })}
                    </th>
                    <th className="text-left">
                      {t('webAdmin.kampane.domainStatus', { locale: 'cs' })}
                    </th>
                    <th className="text-left">{t('webAdmin.kampane.domainRole', { locale: 'cs' })}</th>
                    <th className="text-right">
                      {t('webAdmin.kampane.domainWeight', { locale: 'cs' })}
                    </th>
                    <th className="text-left">{t('webAdmin.kampane.warmup', { locale: 'cs' })}</th>
                    <th className="text-right">
                      {t('webAdmin.kampane.sentToday', { locale: 'cs' })}
                    </th>
                    <th className="text-right">
                      {t('webAdmin.kampane.dailyCapToday', { locale: 'cs' })}
                    </th>
                    <th className="text-left">{t('webAdmin.kampane.brake', { locale: 'cs' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.domains.map((domain) => (
                    <DomainRow key={domain.domain} domain={domain} />
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function CampaignCard(props: {
  readonly campaign: EmailCampaignDto;
  readonly busy: boolean;
  readonly onStop: () => void;
  readonly onStart: () => void;
  readonly onCap: (dailyCap: number) => void;
}): React.JSX.Element {
  const { campaign, busy } = props;
  const running = campaign.status === 'spustena';
  return (
    <section className="mt-6 rounded border p-4" data-testid={`kampane-card-${campaign.id}`}>
      <h2 className="text-lg font-semibold">{campaign.name}</h2>
      <p data-testid={`kampane-status-${campaign.id}`}>
        {t('webAdmin.kampane.status', { locale: 'cs', values: { status: campaign.status } })}
      </p>
      {campaign.statusReason !== null && <p className="text-sm">{campaign.statusReason}</p>}

      <Funnel campaign={campaign} />

      <p className="mt-2 text-sm">
        {t('webAdmin.kampane.deliveries', {
          locale: 'cs',
          values: {
            bounced: String(campaign.bounced),
            complained: String(campaign.complained),
            unsubscribed: String(campaign.unsubscribed),
          },
        })}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {running ? (
          <button
            type="button"
            className="rh-btn"
            disabled={busy}
            onClick={props.onStop}
            data-testid={`kampane-stop-${campaign.id}`}
          >
            {t('webAdmin.kampane.stop', { locale: 'cs' })}
          </button>
        ) : (
          <button
            type="button"
            className="rh-btn"
            disabled={busy}
            onClick={props.onStart}
            data-testid={`kampane-start-${campaign.id}`}
          >
            {t('webAdmin.kampane.start', { locale: 'cs' })}
          </button>
        )}

        <label className="ml-2 text-sm">
          {t('webAdmin.kampane.cap', { locale: 'cs' })}
          <select
            className="ml-1"
            defaultValue={String(campaign.dailyCapOverride ?? 50)}
            disabled={busy}
            data-testid={`kampane-cap-${campaign.id}`}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (Number.isFinite(value) && value > 0) props.onCap(value);
            }}
          >
            {CAP_PLAN.map((cap) => (
              <option key={cap} value={String(cap)}>
                {String(cap)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <TemplatePreview campaignId={campaign.id} />
    </section>
  );
}

const VARIANTS = ['A', 'B', 'C'] as const;

/**
 * Krok KAMP3 bod 4 — náhled e-mailu PŘESNĚ tak, jak odejde (patička,
 * odhlášení, hlavičky), pro každou variantu A/B/C.
 */
function TemplatePreview(props: { readonly campaignId: string }): React.JSX.Element {
  const [variant, setVariant] = React.useState<(typeof VARIANTS)[number] | null>(null);
  const [preview, setPreview] = React.useState<EmailTemplatePreviewDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(
    async (v: (typeof VARIANTS)[number]) => {
      setVariant(v);
      setPreview(null);
      setError(null);
      setLoading(true);
      try {
        setPreview(await getEmailCampaignTemplatePreview(props.campaignId, v));
      } catch (e) {
        setError(
          e instanceof DomainApiError
            ? e.message
            : t('webAdmin.kampane.previewError', { locale: 'cs' }),
        );
      } finally {
        setLoading(false);
      }
    },
    [props.campaignId],
  );

  return (
    <div className="mt-4 border-t pt-3">
      <div className="flex flex-wrap gap-2">
        {VARIANTS.map((v) => (
          <button
            key={v}
            type="button"
            className="rh-btn"
            disabled={loading && variant === v}
            onClick={() => void load(v)}
            data-testid={`kampane-preview-${props.campaignId}-${v}`}
          >
            {t('webAdmin.kampane.previewButton', { locale: 'cs', values: { variant: v } })}
          </button>
        ))}
      </div>
      {error !== null && (
        <p role="alert" className="mt-2 text-sm text-red-600" data-testid="kampane-preview-error">
          {error}
        </p>
      )}
      {preview !== null && (
        <div
          className="mt-3 rounded border bg-gray-50 p-3 text-sm"
          data-testid={`kampane-preview-content-${props.campaignId}`}
        >
          <p className="font-semibold" data-testid="kampane-preview-subject">
            {preview.subject}
          </p>
          {!preview.active && (
            <p className="text-xs text-amber-700">
              {t('webAdmin.kampane.previewInactive', { locale: 'cs' })}
            </p>
          )}
          <pre className="mt-2 whitespace-pre-wrap font-sans" data-testid="kampane-preview-text">
            {preview.text}
          </pre>
          <p className="mt-2 text-xs text-gray-500" data-testid="kampane-preview-unsubscribe-header">
            {t('webAdmin.kampane.previewUnsubscribeHeader', {
              locale: 'cs',
              values: { value: preview.headers['List-Unsubscribe'] ?? '' },
            })}
          </p>
        </div>
      )}
    </div>
  );
}

function Funnel(props: { readonly campaign: EmailCampaignDto }): React.JSX.Element {
  const { funnel } = props.campaign;
  return (
    <ul className="mt-2 space-y-1 text-sm" data-testid={`kampane-funnel-${props.campaign.id}`}>
      {funnel.map((stage) => (
        <li key={stage.stage} data-testid={`kampane-funnel-${props.campaign.id}-${stage.stage}`}>
          {stage.stage}: {stage.count} ({stage.pct} %)
        </li>
      ))}
    </ul>
  );
}

function DomainRow(props: { readonly domain: SendingDomainDto }): React.JSX.Element {
  const { domain } = props;
  return (
    <tr data-testid={`kampane-domain-${domain.domain}`}>
      <td>{domain.domain}</td>
      <td>{domain.status}</td>
      <td data-testid={`kampane-domain-role-${domain.domain}`}>
        {domain.role === 'warm'
          ? t('webAdmin.kampane.roleWarm', { locale: 'cs' })
          : t('webAdmin.kampane.roleNew', { locale: 'cs' })}
      </td>
      <td className="text-right">{domain.weight}</td>
      <td>
        {domain.status === 'zahrev'
          ? t('webAdmin.kampane.warmupPlan', {
              locale: 'cs',
              values: { days: String(domain.warmupDays), cap: String(domain.warmupFirstDayCap) },
            })
          : t('webAdmin.kampane.warmupOff', { locale: 'cs' })}
      </td>
      <td className="text-right" data-testid={`kampane-domain-sent-${domain.domain}`}>
        {domain.sentToday}
      </td>
      <td className="text-right" data-testid={`kampane-domain-cap-${domain.domain}`}>
        {domain.dailyCapToday}
      </td>
      <td data-testid={`kampane-domain-brake-${domain.domain}`}>
        {domain.brakeEngaged
          ? t('webAdmin.kampane.brakeEngaged', { locale: 'cs' })
          : t('webAdmin.kampane.brakeOk', {
              locale: 'cs',
              values: { bounce: String(domain.bouncePct), complaint: String(domain.complaintPct) },
            })}
      </td>
    </tr>
  );
}
